/**
 * SupportLocationContext — a frontend-safe, plain-string channel the screen editor uses
 * to publish the currently-open screen (and optional project/onboarding names) DOWN to
 * the guide popover, which renders far below it.
 *
 * The popover reads this via `useSupportLocation()` to enrich its "copy my location"
 * address with the one piece that is NOT in the URL: which screen is being edited. The
 * default value is an empty snapshot, so guides shown outside the editor degrade
 * gracefully to a URL-only address.
 *
 * Carries plain strings only — NO reactFlow types — so the `guides` module stays
 * decoupled from its consumers (the editor maps `FlowStepNode` → strings at the seam).
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { SupportLocationSnapshot } from './buildSupportLocationText';

const SupportLocationContext = createContext<SupportLocationSnapshot>({});

export interface SupportLocationProviderProps {
  value: SupportLocationSnapshot;
  children: React.ReactNode;
}

/**
 * Publishes a location snapshot, MERGED with any parent provider — so an
 * onboarding-level provider (project/onboarding) and a nested screen-level provider
 * (the open screen) accumulate into one full address rather than overwriting each
 * other. `undefined` fields never clobber an inherited value.
 */
export const SupportLocationProvider: React.FC<SupportLocationProviderProps> = ({ value, children }) => {
  const parent = useContext(SupportLocationContext);
  const merged = useMemo<SupportLocationSnapshot>(() => {
    const next: SupportLocationSnapshot = { ...parent };
    for (const [key, val] of Object.entries(value)) {
      if (val !== undefined && val !== null && val !== '') {
        (next as Record<string, unknown>)[key] = val;
      }
    }
    return next;
  }, [parent, value]);
  return <SupportLocationContext.Provider value={merged}>{children}</SupportLocationContext.Provider>;
};

/** Read the current in-editor location snapshot ({} when no provider is mounted). */
export function useSupportLocation(): SupportLocationSnapshot {
  return useContext(SupportLocationContext);
}
